import React from "react";
import { Calendar as CalendarIcon, Clock, CheckCircle2, ShieldCheck, Phone, CreditCard, ChevronRight, ChevronLeft } from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import { useSiteContent } from "../admin/SiteContentContext";
import PaymentOptions from "../components/PaymentOptions";

const TIMES = ["9:00 AM", "10:30 AM", "12:00 PM", "1:30 PM", "3:00 PM", "4:30 PM"];

export default function Booking() {
  const { content } = useSiteContent();
  const { siteInfo, servicesList, paymentSettings } = content;

  const [tab, setTab] = React.useState("consult");
  const [date, setDate] = React.useState(undefined);
  const [time, setTime] = React.useState(null);
  const [form, setForm] = React.useState({ name: "", phone: "", email: "", note: "" });
  const [serviceIdx, setServiceIdx] = React.useState(0);
  const [hours, setHours] = React.useState(2);
  const [step, setStep] = React.useState("details"); // details -> pay -> done
  const [bookingId, setBookingId] = React.useState(null);

  const service = servicesList[serviceIdx] || servicesList[0];
  const amount = tab === "consult" ? Number(paymentSettings.consultationDeposit || 0)
    : Number((service?.price || 0) * (Number(hours) || 1));
  const purpose = tab === "consult"
    ? "Care consultation deposit"
    : `${service?.title} · ${hours} ${service?.unit || "hr"}${hours > 1 ? "s" : ""}`;

  const resetAll = () => {
    setStep("details"); setDone(false); setBookingId(null);
    setDate(undefined); setTime(null); setHours(2);
    setForm({ name: "", phone: "", email: "", note: "" });
  };
  const [done, setDone] = React.useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!date || !time) return;
    const id = `BK-${Date.now().toString(36).toUpperCase()}`;
    try {
      const bookings = JSON.parse(localStorage.getItem("3e_bookings") || "[]");
      bookings.push({
        id,
        ...form,
        date: date?.toISOString?.() ?? String(date),
        time,
        tab,
        service: tab === "service" ? { title: service?.title, hours, rate: service?.price, unit: service?.unit } : null,
        amount,
        at: Date.now(),
        paymentStatus: amount > 0 ? "pending" : "not-required",
      });
      localStorage.setItem("3e_bookings", JSON.stringify(bookings));
    } catch {}
    setBookingId(id);
    if (amount > 0) setStep("pay");
    else { setDone(true); setStep("done"); }
  };

  const markPaid = (record) => {
    try {
      const list = JSON.parse(localStorage.getItem("3e_bookings") || "[]");
      const next = list.map(b => b.id === bookingId ? { ...b, paymentStatus: "reported", paymentMethod: record.method, paymentRef: record.reference } : b);
      localStorage.setItem("3e_bookings", JSON.stringify(next));
      // also link payment to booking
      const pays = JSON.parse(localStorage.getItem("3e_payments") || "[]");
      const updated = pays.map((p, i) => i === pays.length - 1 ? { ...p, bookingId } : p);
      localStorage.setItem("3e_payments", JSON.stringify(updated));
    } catch {}
    setDone(true); setStep("done");
  };

  return (
    <main>
      <section className="py-14">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <span className="kicker">Appointments & Care</span>
          <h1 className="hero-heading text-white text-5xl md:text-6xl mt-5">Schedule your session.</h1>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto mt-6 leading-relaxed">Select a convenient time for a care consultation, or directly book one of our non-medical services.</p>

          <div className="inline-flex mt-8 p-1.5 rounded-full bg-white/5 border border-white/10">
            <button onClick={() => setTab("consult")}
              className={`px-5 py-2 rounded-full text-sm transition ${tab === "consult" ? "bg-[#c7e34a] text-[#0b1220]" : "text-slate-300 hover:text-white"}`}>
              Care Consultation
            </button>
            <button onClick={() => setTab("service")}
              className={`px-5 py-2 rounded-full text-sm transition ${tab === "service" ? "bg-[#c7e34a] text-[#0b1220]" : "text-slate-300 hover:text-white"}`}>
              Book a Service
            </button>
          </div>

          <div className="flex items-center justify-center gap-2 mt-8 text-xs uppercase tracking-widest">
            <StepPill num={1} label="Details" active={step === "details"} done={step !== "details"} />
            <ChevronRight className="w-3 h-3 text-slate-500" />
            <StepPill num={2} label="Payment" active={step === "pay"} done={step === "done"} />
            <ChevronRight className="w-3 h-3 text-slate-500" />
            <StepPill num={3} label="Confirmed" active={step === "done"} done={false} />
          </div>
        </div>
      </section>

      {step !== "pay" && (
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-8">
          <div className="card-soft p-7">
            <div className="flex items-center gap-2 mb-5">
              <CalendarIcon className="w-5 h-5 text-[#c7e34a]" />
              <h3 className="text-white font-semibold">Scheduling Calendar</h3>
            </div>
            <p className="text-slate-400 text-sm mb-5">Select your preferred day</p>

            <div className="bg-[#0b1220] rounded-2xl p-3 border border-white/5 flex justify-center">
              <Calendar mode="single" selected={date} onSelect={setDate}
                disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} className="text-slate-200" />
            </div>

            {date && (
              <div className="mt-6">
                <div className="flex items-center gap-2 mb-3 text-slate-300 text-sm">
                  <Clock className="w-4 h-4" /> Pick a time
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {TIMES.map(t => (
                    <button key={t} onClick={() => setTime(t)}
                      className={`py-2.5 rounded-xl border text-sm transition ${time === t ? "bg-[#c7e34a] text-[#0b1220] border-[#c7e34a]" : "bg-white/5 border-white/10 text-slate-200 hover:border-[#c7e34a]/40"}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="card-soft mt-6 p-4 flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-[#c7e34a] shrink-0 mt-0.5" />
              <div>
                <h4 className="text-white text-sm font-semibold">Secure & Confidential</h4>
                <p className="text-slate-400 text-xs mt-1">Your information is encrypted and securely processed. We only use this to prepare for your requested care.</p>
              </div>
            </div>
          </div>

          <div className="card-soft p-7">
            <h3 className="text-white font-semibold mb-5">{tab === "consult" ? "Consultation Details" : "Service Details"}</h3>

            {done ? (
              <div className="text-center py-10">
                <div className="w-14 h-14 rounded-full bg-[#c7e34a]/15 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-7 h-7 text-[#c7e34a]" />
                </div>
                <h4 className="font-serif-display text-white text-2xl mt-5">Thank you.</h4>
                <p className="text-slate-300 mt-2 max-w-sm mx-auto">We've received your request. A member of the 3E DanCare team will reach out shortly to confirm.</p>
                {bookingId && <p className="text-xs text-slate-500 mt-3">Reference: <span className="text-[#c7e34a]">{bookingId}</span></p>}
                <button onClick={resetAll} className="btn-ghost mt-6">Book another</button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                {tab === "service" && (
                  <div className="space-y-3 pb-2">
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Choose a service *</label>
                      <select value={serviceIdx} onChange={e => setServiceIdx(Number(e.target.value))}
                        className="w-full bg-[#0b1220] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#c7e34a]/60">
                        {servicesList.map((s, i) => (
                          <option key={i} value={i}>{s.title} — ${s.price}/{s.unit}</option>
                        ))}
                      </select>
                    </div>
                    <div className="grid grid-cols-[1fr,auto] items-end gap-3">
                      <div>
                        <label className="text-xs text-slate-400 mb-1 block">Hours *</label>
                        <input type="number" min={1} max={12} value={hours}
                          onChange={e => setHours(Math.max(1, Math.min(12, Number(e.target.value) || 1)))}
                          className="w-full bg-[#0b1220] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#c7e34a]/60" />
                      </div>
                      <div className="bg-[#0b1220] border border-white/10 rounded-xl px-4 py-3">
                        <div className="text-[11px] text-slate-500 uppercase tracking-widest">Estimated</div>
                        <div className="text-white font-semibold">${amount.toFixed(2)}</div>
                      </div>
                    </div>
                  </div>
                )}

                {tab === "consult" && paymentSettings.consultationDeposit > 0 && (
                  <div className="bg-[#0b1220] border border-white/10 rounded-xl px-4 py-3 flex items-center justify-between">
                    <div>
                      <div className="text-[11px] text-slate-500 uppercase tracking-widest">Consultation deposit</div>
                      <div className="text-xs text-slate-400">Applied to your first scheduled service</div>
                    </div>
                    <div className="text-white font-semibold">${Number(paymentSettings.consultationDeposit).toFixed(2)}</div>
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Full Name *</label>
                    <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                      className="w-full bg-[#0b1220] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-[#c7e34a]/60"
                      placeholder="Your name" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Phone Number *</label>
                    <input required value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                      className="w-full bg-[#0b1220] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-[#c7e34a]/60"
                      placeholder="(xxx) xxx-xxxx" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Email Address *</label>
                  <input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                    className="w-full bg-[#0b1220] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-[#c7e34a]/60"
                    placeholder="you@email.com" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">What would you like to discuss?</label>
                  <textarea rows={4} value={form.note} onChange={e => setForm({...form, note: e.target.value})}
                    className="w-full bg-[#0b1220] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-[#c7e34a]/60"
                    placeholder="Tell us a little about your family's needs…" />
                </div>

                <button type="submit" disabled={!date || !time}
                  className={`w-full justify-center ${!date || !time ? "btn-ghost opacity-70" : "btn-lime"}`}>
                  {!date || !time ? "Please select date & time" : (amount > 0 ? <>Continue to payment <ChevronRight className="w-4 h-4" /></> : "Request")}
                </button>

                <div className="text-center text-xs text-slate-500 pt-2">
                  Prefer to call? <a href={`tel:${siteInfo.phoneRaw || siteInfo.phone}`} className="text-[#c7e34a] inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {siteInfo.phone}</a>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
      )}

      {step === "pay" && (
      <section className="pb-24">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <div className="card-soft p-7">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="w-5 h-5 text-[#c7e34a]" />
              <h3 className="text-white font-semibold">Complete your payment</h3>
            </div>
            <p className="text-slate-400 text-sm mb-5">Choose a method you prefer. We review every payment manually and confirm by phone or email.</p>

            <div className="bg-[#0b1220] border border-white/10 rounded-xl p-4 mb-5 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Booking</span>
                <span className="text-slate-200">{bookingId}</span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-slate-400">When</span>
                <span className="text-slate-200">{(date?.toLocaleDateString?.() || "")} · {time}</span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-slate-400">For</span>
                <span className="text-slate-200">{purpose}</span>
              </div>
            </div>

            <PaymentOptions amount={amount} purpose={purpose}
              meta={{ name: form.name, phone: form.phone, email: form.email, bookingId }}
              onConfirmed={markPaid} />

            <button onClick={() => setStep("details")} className="btn-ghost mt-6 text-sm">
              <ChevronLeft className="w-4 h-4" /> Back to details
            </button>
          </div>
        </div>
      </section>
      )}
    </main>
  );
}

function StepPill({ num, label, active, done }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] ${active ? "bg-[#c7e34a] text-[#0b1220] font-semibold" : done ? "bg-[#c7e34a]/15 text-[#c7e34a] border border-[#c7e34a]/30" : "bg-white/5 text-slate-400 border border-white/10"}`}>
      <span className={`w-4 h-4 rounded-full inline-flex items-center justify-center ${active || done ? "bg-[#0b1220]/15 text-current" : "bg-white/10 text-slate-300"}`}>{num}</span>
      {label}
    </span>
  );
}
